<template>
  <div
    id="liveContainer"
    ref="liveContainer"
    :class="containerClassName"
  >
    <div class="header">
      <div class="header-section">
        <div :class="`${output}-indicator`">
          <span>
            {{ output === 'live' ? 'L' : 'P' }}
          </span>
        </div>

        <template v-if="showVideoDuration">
          <div class="countdown margin-left-md">
            {{ videoTimestamp | toMinutesAndSeconds }} / {{ videoDuration | toMinutesAndSeconds }}
          </div>
        </template>
      </div>

      <a
        :title="[collapsed ? 'Expand window' : 'Collapse window']"
        class="tb-btn tb-btn-text sm medium-em header-section"
        data-entity="producer-toggleLiveCollapse"
        @click="toggleCollapse"
      >
        <span class="icon" :class="[collapsed ? 'fas fa-expand-alt' : 'fas fa-compress-alt']" />
      </a>
    </div>

    <div
      id="liveHeightContainer"
      ref="liveHeightContainer"
      :class="`${output}-height-container`"
    >
      <div
        ref="liveIframeWrapper"
        :class="iframeWrapperClassName"
      >
        <iframe
          ref="liveIframe"
          :class="`${output}-iframe`"
          allow="autoplay"
          :src="output === 'live' ? liveUrl : previewUrl"
        />
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { mapState, mapGetters } from 'vuex';

import Timer from '../utils/timer';
import Analytics from '../utils/analytics';
import TimelineMixins from '../mixins/timeline-mixins';

const getLocalStorageKey = (output = 'live') => `previewCollapsed_${output}`;

export default {
  props: {
    output: {
      type: String,
      default: 'live',
    },
  },

  mixins: [TimelineMixins],

  data() {
    return {
      videoTimestamp: 0,
      collapsed: window.localStorage.getItem(getLocalStorageKey(this.output)) === 'true',
      resizeObserver: undefined,
      savedWrapperSize: {},
    };
  },

  mounted() {
    this.handleOnResizeWindow(undefined);
    this.initializeResizeObserver();

    this.$store.dispatch('setupLive');
    this.$store.dispatch('setupPreview');

    this.startTimer();
  },

  beforeDestroy() {
    this.unobserve();
    this.resetTimer();
  },

  computed: {
    ...mapGetters(['liveUrl', 'previewUrl']),

    ...mapState({
      videoDuration: (state) => state.production.videoDuration,
      videoPlaying: (state) => state.production.production.videoPlaying,
    }),

    production() {
      return this.output === 'live'
        ? this.$store.state.production.production
        : this.$store.state.preview.production;
    },

    containerClassName() {
      return {
        [`${this.output}-container`]: true,
        collapsed: this.collapsed,
      };
    },

    iframeWrapperClassName() {
      const { output } = this;

      const isActive = output === 'live' ? this.liveDisplayActive : this.previewDisplayActive;

      return {
        [`${output}-iframe-wrapper`]: true,
        [`${output}-active`]: isActive,
        [`${output}-empty`]: !isActive,
      };
    },

    showVideoDuration() {
      // Only show countdown if we have a video duration
      // Might need to add some further checks since producer doesn't know what the post is right now.
      return (
        this.videoDuration
        && this.isLive
      );
    },

    isLive() {
      const { index, sub_index: subIndex } = this.production;
      return !!(index, subIndex);
    },

    /**
    * Unique string to represent live element + live post
    */
    liveKey() {
      const { index, sub_index: subIndex } = this.production;
      return `${index}:${subIndex}`;
    },
  },

  watch: {
    // If the live element changes, then reset everything
    liveKey() {
      this.resetTimer();
    },

    videoPlaying(videoPlaying) {
      if (!this.timer) {
        // Start a new timer
        this.startTimer();
        return;
      }

      // Start/Stop the existing timer
      if (videoPlaying) {
        this.timer.start();
      } else {
        this.timer.pause();
      }
    },

    videoDuration() {
      // If video duration changes, then we need a new timer entirely.
      // Because videos don't magically change their own duration.
      this.startTimer();
    },
  },

  methods: {
    /**
    * If there's a video playing and we have a video duration, then start timer.
    * Every tick of the timer will update `videoTimestamp` with the current time.
    * This is just a rough estimate of where the video is at in the output(s)
    */
    startTimer() {
      // Stop any existing timer so we stop the tick events
      this.resetTimer();

      if (!this.videoPlaying || !this.videoDuration) {
        return;
      }

      this.timer = new Timer({
        duration: this.videoDuration,

        onTick: (ts) => {
          this.videoTimestamp = ts;
        },

        onDone: () => {
          this.videoTimestamp = this.videoDuration;
          this.resetTimer();
        },
      });
    },

    resetTimer() {
      this.timer?.pause();
      this.timer = null;
      this.videoTimestamp = 0;
    },

    toggleCollapse() {
      const willBeCollapsed = !this.collapsed;
      const wrapper = this.$refs.liveIframeWrapper;

      if (willBeCollapsed) {
        this.unobserve();
        const { height, width } = wrapper.getBoundingClientRect();
        this.savedWrapperSize = { height, width };
      }

      if (!willBeCollapsed) {
        wrapper.style.height = this.savedWrapperSize.height;
        wrapper.style.width = this.savedWrapperSize.width;
        setTimeout(() => {
          this.initializeResizeObserver();
        }, 275); // transition time is 250 so turn it back on shortly after
      }

      // do the toggle
      this.collapsed = willBeCollapsed;
      window.localStorage.setItem(getLocalStorageKey(this.output), this.collapsed);

      Analytics.log({
        event: `Producer ${this.collapsed ? 'Collapsed' : 'Expanded'} Live`,
        fields: {
          production_id: this.production._id,
        },
        ownerId: this.production.owner,
      });
    },

    initializeResizeObserver() {
      // resize observer only runs once per animation frame, so there
      // is little need to debounce this observer.
      this.resizeObserver = new ResizeObserver(this.handleOnResizeWindow);
      this.resizeObserver.observe(this.$refs.liveContainer);
    },

    handleOnResizeWindow() {
      const heightContainer = this.$refs.liveHeightContainer;
      const wrapper = this.$refs.liveIframeWrapper;
      const iframe = this.$refs.liveIframe;
      if (!iframe) { return; }
      const aspectRatio = 16 / 9;
      // this forces a 16/9 aspect ratio. So if the content being rendered in the iframe
      // isn't responsive, or 1920x1080, we are potentially going to clip the content
      iframe.style.width = '1920px';
      iframe.style.height = '1080px';
      // ensures aspect ratio doesn't change from 16:9. Aspect ratio
      // is set to 16/9 in preview-window.scss on iframe-wrapper
      const { width: hcw, height: hch } = heightContainer.getBoundingClientRect();
      const heightContainerAspectRatio = hcw / hch;

      if (heightContainerAspectRatio < aspectRatio) {
        wrapper.style.width = `${Math.floor(hcw)}px`;
        wrapper.style.height = 'auto';
      } else {
        wrapper.style.width = 'auto';
        wrapper.style.height = `${Math.floor(hch)}px`;
      }

      // sizing the iframe to fit into the container that we previously
      // sized to 1920/1080 (16/9)
      const fw = iframe.clientWidth; // will be 1920px

      // wait for sizing to render then calculate scale
      this.$nextTick(() => {
        const { width: ww } = wrapper.getBoundingClientRect();
        const scale = Math.ceil(ww) / fw;

        iframe.style.transform = `scale(${scale})`;
      });
    },

    unobserve() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(this.$refs.liveContainer);
      }
    },
  },
};
</script>
